import translate from '@translate';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { ProviderType } from 'src/types/provider';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import TopBlock from './TopBlock';
import { EDIT_KEYS, EditKeysType } from './constants';
import RenderComponents from './RenderComponents';
import { clearProviderDetail, getProviderDetailFetch } from 'src/resources/providerDetail/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import {
    getShopifyPaymentMethodsFetch, getShopifyShippingMethodsFetch,
    getShoptetPaymentMethodsFetch,
    getShoptetShippingMethodsFetch,
    getShoptetStatusesFetch,
    getWoocommercePaymentMethodsFetch,
    getWoocommerceShippingMethodsFetch,
    saveSkladonMappingFetch
} from 'src/resources/integration/reducer';
import { ConstantsCodeType } from '../../types/constantsCodes';
import { SkladonMappingType } from '../../types/skladonMapping';
import { getSkladonMappingListFetch } from '../../resources/skladonMappingList/reducer';
import { ShippingMethodsEditKeysType, ShippingMethodsType } from './ShippingMethodsTable';
import { MappingPaymentsEditKeysType, MappingPaymentType } from './PaymentMethodsTable';
import { OthersEditKeysType } from './OthersTable';
import { getProviderServiceCodesFetchRequest } from '../../resources/user/sagas';
import { UserServiceCode } from '../../types/user';
import { clearProviderConfigDetail, getProviderConfigDetailFetch } from '../../resources/providerConfigDetail/reducer';
import IntegrationMappingHistory from './IntegrationMappingHistory';
import { IntegrationMappingHistoryFormatContext } from './integrationMappingHistoryCellFormat';

export type FormErrorsType = { [key: string]: boolean };

/** `0` / `'0'` (dobírka, ne) jsou platné hodnoty. Prázdné je jen null, undefined a ''. */
const mappingFieldOrDraft = (stored: unknown, draft: unknown, fallback: string): string => {
    if (stored !== undefined && stored !== null && stored !== '') {
        return String(stored);
    }
    if (draft !== undefined && draft !== null && draft !== '') {
        return String(draft);
    }
    return fallback;
};

export type OnChangeType = (key: EditKeysType, value: any, error: boolean) => void;

const initData = { ACTIVE: 1, DEFAULT_ALLOW_EXPEDITION: 1, PROVIDER_USERS: [], SERVICE_CODES: [] };
const IntegrationDetailContainer: FC = () => {
    const dispatch = useDispatch();

    const { idProvider } = useParams();

    const formRef = useRef<HTMLFormElement>(null);
    const provider = useSelector((state: RootStoreType) => state.providerDetail.data);
    const integrationConfigData = useSelector((state: RootStoreType) => state.providerConfigDetail.data);
    const providerLoading = useSelector((state: RootStoreType) => state.providerDetail.loading);
    const shoptetStatuses = useSelector((state: RootStoreType) => state.integrationDetail.shoptetStatuses);
    const shoptetStatusesLoading = useSelector((state: RootStoreType) => state.integrationDetail.shoptetStatusesLoading);
    const shoptetShippingMethods = useSelector((state: RootStoreType) => state.integrationDetail.shoptetShippingMethods);
    const shoptetShippingMethodsLoading = useSelector((state: RootStoreType) => state.integrationDetail.shoptetShippingMethodsLoading);
    const shoptetPaymentMethods = useSelector((state: RootStoreType) => state.integrationDetail.shoptetPaymentMethods);
    const shoptetPaymentMethodsLoading = useSelector((state: RootStoreType) => state.integrationDetail.shoptetPaymentMethodsLoading);
    const shopifyPaymentMethods = useSelector((state: RootStoreType) => state.integrationDetail.shopifyPaymentMethods);
    const shopifyPaymentMethodsLoading = useSelector((state: RootStoreType) => state.integrationDetail.shopifyPaymentMethodsLoading);
    const shopifyShippingMethods = useSelector((state: RootStoreType) => state.integrationDetail.shopifyShippingMethods);
    const shopifyShippingMethodsLoading = useSelector((state: RootStoreType) => state.integrationDetail.shopifyShippingMethodsLoading);
    const woocommercePaymentMethods = useSelector((state: RootStoreType) => state.integrationDetail.woocommercePaymentMethods);
    const woocommercePaymentMethodsLoading = useSelector((state: RootStoreType) => state.integrationDetail.woocommercePaymentMethodsLoading);
    const woocommerceShippingMethods = useSelector((state: RootStoreType) => state.integrationDetail.woocommerceShippingMethods);
    const woocommerceShippingMethodsLoading = useSelector((state: RootStoreType) => state.integrationDetail.woocommerceShippingMethodsLoading);
    const skladonMappingDataLoading = useSelector((state: RootStoreType) => state.skladonMappingList.loading);
    const saveLoading = useSelector((state: RootStoreType) => state.integrationDetail.saveLoading);
    const integrationSaveError = useSelector((state: RootStoreType) => state.integrationDetail.error);
    const skladonMappingData = useSelector((state: RootStoreType) => state.skladonMappingList.data);
    const [providerServiceCodes, setProviderServiceCodes] = useState<UserServiceCode[]>([]);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const paymentMethodCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.paymentCodes);
    const orderStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.orderStatusCodes);
    const shipperCodes = useSelector((state: RootStoreType) =>
        [...state.constantsCodes.data.shipperCodes].sort((a, b) => a.label.localeCompare(b.label))
    );
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes);
    const [editData, setEditData] = useState<ProviderType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [activeTab, setActiveTab] = useState<string>('statuses');
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [statusesTableData, setStatusesTableData] = useState<SkladonMappingType[]>([]);
    const [shippingMethodsTableData, setShippingMethodsTableData] = useState<ShippingMethodsType[]>([]);
    const [paymentMethodsTableData, setPaymentMethodsTableData] = useState<MappingPaymentType[]>([]);
    const [productTypeData, setProductTypeData] = useState<SkladonMappingType | null>(null);
    const [orderTypeData, setOrderTypeData] = useState<SkladonMappingType[]>([]);
    const [retinoData, setRetinoData] = useState<SkladonMappingType[]>([]);

    const [isStatusesTableDataChanging, setIsStatusesTableDataChanging] = useState<boolean>(false);
    const [isShippingTableDataChanging, setIsShippingTableDataChanging] = useState<boolean>(false);
    const [isPaymentTableDataChanging, setIsPaymentTableDataChanging] = useState<boolean>(false);
    const [isOthersTableDataChanging, setIsOthersTableDataChanging] = useState<boolean>(false);
    const [orderTypeIdsToDelete, setOrderTypeIdsToDelete] = useState<number[]>([]);
    const [retinoIdsToDelete, setRetinoIdsToDelete] = useState<number[]>([]);
    const [hasRetinoConfig, setHasRetinoConfig] = useState<boolean>(false);
    const [historyReloadKey, setHistoryReloadKey] = useState(0);
    const wasSavingRef = useRef(false);

    const integrationHistoryFormatContext: IntegrationMappingHistoryFormatContext = useMemo(
        () => ({
            orderStatuses: orderStatusCodes ?? [],
            boolNumberCodes: boolNumberCodes ?? [],
            shipperCodes: shipperCodes ?? [],
            shipperServiceCodes: serviceCodes ?? [],
            paymentMethodCodes: paymentMethodCodes ?? []
        }),
        [orderStatusCodes, boolNumberCodes, shipperCodes, serviceCodes, paymentMethodCodes]
    );

    useEffect(() => {

        if (!provider || !skladonMappingData || activeTab !== 'statuses' || providerLoading || shoptetStatusesLoading || skladonMappingDataLoading) {
            return;
        }

        setIsStatusesTableDataChanging(true);
        let uidStatuses: ConstantsCodeType[] = [];

        if (provider.SYSTEM === 'SHOPTET') {
            if (shoptetStatuses) {
                uidStatuses = shoptetStatuses.map((status) => ({
                    value: status.id,
                    label: status.name
                }));
            } else {
                setIsStatusesTableDataChanging(false);
                return;
            }
        } else if (provider.SYSTEM === 'SHOPIFY') {
            uidStatuses = [
                { value: 'unfulfilled', label: 'Unfulfilled' },
                { value: 'fulfilled', label: 'Fulfilled' },
                { value: 'canceled', label: 'Canceled' }
            ];
        } else if (provider.SYSTEM === 'WOOCOMMERCE') {
            uidStatuses = [
                { value: 'pending', label: 'pending' },
                { value: 'processing', label: 'processing' },
                { value: 'on-hold', label: 'on-hold' },
                { value: 'completed', label: 'completed' },
                { value: 'cancelled', label: 'cancelled' },
                { value: 'refunded', label: 'refunded' },
                { value: 'failed', label: 'failed' },
                { value: 'trash', label: 'trash' }
            ];
        }

        if (uidStatuses.length === 0) {
            setIsStatusesTableDataChanging(false);
            return;
        }

        let newId = Math.min.apply(0, [0, ...skladonMappingData.map((item) => item.ID || 0)]) - 1;
        const data: SkladonMappingType[] = uidStatuses.map((uid, index) => {
            const found = skladonMappingData.find((row) => row.UID === String(uid.value) && row.KEY === 'ORDER_STATUS');
            newId = newId - index;

            let defaultImport = '1';
            let defaultExport = '1';
            let defaultValue = '';

            if (provider.SYSTEM === 'SHOPIFY' && uid.value === 'canceled') {
                defaultExport = '0';
            }

            // WOOCOMMERCE default mapping podle poskytnutého příkladu
            if (provider.SYSTEM === 'WOOCOMMERCE') {
                switch (uid.value) {
                    case 'pending':
                        defaultValue = 'waitingForPayment';
                        defaultImport = '1';
                        defaultExport = '1';
                        break;
                    case 'processing':
                        defaultValue = 'preReceived';
                        defaultImport = '1';
                        defaultExport = '0';
                        break;
                    case 'on-hold':
                        defaultValue = 'suspended';
                        defaultImport = '1';
                        defaultExport = '0';
                        break;
                    case 'completed':
                        defaultValue = 'onTheWay';
                        defaultImport = '0';
                        defaultExport = '1';
                        break;
                    case 'cancelled':
                        defaultValue = 'canceled';
                        defaultImport = '1';
                        defaultExport = '1';
                        break;
                    case 'refunded':
                        defaultValue = 'lost';
                        defaultImport = '0';
                        defaultExport = '0';
                        break;
                    case 'failed':
                        defaultValue = 'lost';
                        defaultImport = '0';
                        defaultExport = '0';
                        break;
                    case 'trash':
                        defaultValue = 'lost';
                        defaultImport = '0';
                        defaultExport = '0';
                        break;
                    default:
                        defaultValue = '';
                }
            }

            return {
                UID: String(uid.value),
                VALUE: found?.VALUE || defaultValue,
                NAME: String(uid.label),
                IMPORT: found?.IMPORT || defaultImport,
                EXPORT: found?.EXPORT || defaultExport,
                ID: found?.ID || newId
            };
        });

        setStatusesTableData(data);
        setIsStatusesTableDataChanging(false);
    }, [provider, skladonMappingData, shoptetStatuses]);

    useEffect(() => {
        if (idProvider && provider) {
            dispatch(getProviderConfigDetailFetch({ idProvider, system: provider.SYSTEM }));
        }
        return () => {
            clearProviderConfigDetail();
        };
    }, [provider, idProvider]);

    // Kontrola, zda má provider konfiguraci pro Retino
    useEffect(() => {
        if (integrationConfigData && integrationConfigData.length > 0) {
            // Hledáme konfiguraci pro Retino
            const retinoConfig = integrationConfigData.find(config =>
                config.ITEM_KEY && config.ITEM_KEY === 'retino.apiToken' && config.VALUE);

            setHasRetinoConfig(!!retinoConfig);
        } else {
            setHasRetinoConfig(false);
        }
    }, [integrationConfigData]);

    useEffect(() => {
        if (!provider || activeTab !== 'shippingMethods') {
            return;
        }
        setIsShippingTableDataChanging(true);
        let uidShippers: ConstantsCodeType[] = [];
        if (provider?.SYSTEM === 'SHOPTET' && shoptetShippingMethods) {
            uidShippers = shoptetShippingMethods.map((shipper) => ({
                value: shipper.guid,
                label: shipper.name
            }));
        }

        if (provider?.SYSTEM === 'SHOPIFY' && shopifyShippingMethods) {
            uidShippers = shopifyShippingMethods.map((shipper) => ({
                value: shipper.name,
                label: shipper.name
            }));
        }

        if (provider?.SYSTEM === 'WOOCOMMERCE' && woocommerceShippingMethods) {
            uidShippers = woocommerceShippingMethods.map((shipper) => ({
                value: shipper.instance_id,
                label: shipper.value
            }));
        }

        const data: ShippingMethodsType[] = uidShippers.map((uid, index) => {
            const foundShipper = skladonMappingData?.find((row) => row.UID === String(uid.value) && row.KEY === 'ORDER_SHIPPER');
            const foundServiceCode = skladonMappingData?.find((row) => row.UID === String(uid.value) && row.KEY === 'ORDER_SHIPPER_SERVICE_CODE');
            const foundBranchId = skladonMappingData?.find((row) => row.UID === String(uid.value) && row.KEY === 'ORDER_BRANCH_ID');
            const foundShippingMethod = shippingMethodsTableData.find((row) => row.ID === index);
            return {
                UID: String(uid.value),
                NAME: String(uid.label),
                SHIPPER: foundShipper?.VALUE || foundShippingMethod?.SHIPPER || '',
                SHIPPER_SERVICE_CODE: foundServiceCode?.VALUE || '',
                BRANCH_ID: foundBranchId?.VALUE || foundShippingMethod?.BRANCH_ID || '',
                IMPORT: foundShipper?.IMPORT || '1',
                ID: index,
                ID_SHIPPER: foundShipper?.ID,
                ID_SHIPPER_SERVICE_CODE: foundServiceCode?.ID,
                ID_BRANCH_ID: foundBranchId?.ID
            };
        });
        setIsShippingTableDataChanging(false);
        setShippingMethodsTableData(data);

    }, [provider, skladonMappingData, shoptetShippingMethods, shopifyShippingMethods, woocommerceShippingMethods, activeTab]);

    useEffect(() => {
        if (!provider || activeTab !== 'paymentMethods') {
            return;
        }
        setIsPaymentTableDataChanging(true);
        let uidPaymentMethods: ConstantsCodeType[] = [];
        if (provider?.SYSTEM === 'SHOPTET' && shoptetPaymentMethods) {
            uidPaymentMethods = shoptetPaymentMethods.map((paymentMethod) => ({
                value: paymentMethod.guid,
                label: paymentMethod.name
            }));
        } else if (provider?.SYSTEM === 'SHOPIFY' && shopifyPaymentMethods) {
            uidPaymentMethods = shopifyPaymentMethods.map((paymentMethod) => ({
                value: paymentMethod,
                label: paymentMethod
            }));
        } else if (provider?.SYSTEM === 'WOOCOMMERCE' && woocommercePaymentMethods) {
            uidPaymentMethods = woocommercePaymentMethods.map((paymentMethod) => ({
                value: paymentMethod.id,
                label: paymentMethod.title
            }));
        }

        const data: MappingPaymentType[] = uidPaymentMethods.map((uid, index) => {
            const foundPaymentMethod = skladonMappingData?.find((row) => row.UID === String(uid.value) && row.KEY === 'ORDER_PAYMENT_METHOD');
            const foundPaid = skladonMappingData?.find((row) => row.UID === String(uid.value) && row.KEY === 'ORDER_PAID');
            const found = paymentMethodsTableData.find((row) => row.ID === index);
            return {
                UID: String(uid.value),
                PAYMENT_METHOD: mappingFieldOrDraft(foundPaymentMethod?.VALUE, found?.PAYMENT_METHOD, ''),
                NAME: String(uid.label),
                PAID: mappingFieldOrDraft(foundPaid?.VALUE, found?.PAID, 'noMapping'),
                IMPORT: mappingFieldOrDraft(foundPaymentMethod?.IMPORT, found?.IMPORT, '1'),
                ID: index,
                ID_PAYMENT_METHOD: foundPaymentMethod?.ID,
                ID_PAID: foundPaid?.ID
            };
        });
        setIsPaymentTableDataChanging(false);
        setPaymentMethodsTableData(data);
    }, [provider, skladonMappingData, shoptetPaymentMethods, shopifyPaymentMethods, woocommercePaymentMethods, activeTab]);

    useEffect(() => {
        if (!provider || activeTab !== 'others') {
            return;
        }
        setIsOthersTableDataChanging(true);

        // Handle PRODUCT_TYPE
        const foundProductType = skladonMappingData?.find((row) => row.KEY === 'PRODUCT_CUSTOM');
        setProductTypeData(foundProductType || {
            UID: 'czechFifoProductDefaults',
            VALUE: 'czechFifoProductDefaults',
            NAME: 'czechFifoProductDefaults',
            KEY: 'PRODUCT_CUSTOM',
            IMPORT: '1',
            EXPORT: '1',
            ID: -1
        });

        // Handle ORDER_TYPE
        const orderTypes = skladonMappingData?.filter((row) => row.KEY === 'ORDER_TYPE') || [];
        let newOrderTypeData: SkladonMappingType[] = [];

        if (orderTypes.length > 0) {
            newOrderTypeData = orderTypes.map((item, index) => {
                return {
                    ...item,
                    ID: item.ID || -1 - index,
                    NAME: item.NAME?.toUpperCase(),
                    VALUE: item.VALUE?.toUpperCase()
                };
            });
        }

        setOrderTypeData(newOrderTypeData);

        // Handle RETINO data if provider has Retino config
        if (hasRetinoConfig) {
            const retinoItems = skladonMappingData?.filter((row) =>
                row.SYS === 'RETINO' ||
                (row.KEY && row.KEY.startsWith('RETINO_'))
            ) || [];

            let newRetinoData: SkladonMappingType[] = [];

            if (retinoItems.length > 0) {
                newRetinoData = retinoItems.map((item, index) => {
                    return {
                        ...item,
                        ID: item.ID || -1 - index,
                        SYS: 'RETINO',
                        IMPORT: '1',
                        EXPORT: '1'
                    };
                });
            }

            setRetinoData(newRetinoData);
        }

        setIsOthersTableDataChanging(false);
    }, [provider, skladonMappingData, activeTab]);

    const changeStatusMappingValue = (key: EditKeysType, value: any, error: boolean, id: number) => {
        const newData = [...statusesTableData];
        const productIndex = newData.findIndex((product) => product.ID === id);
        if (productIndex !== -1) {
            newData[productIndex] = { ...newData[productIndex], [key]: value };
        }
        setStatusesTableData(newData);
    };

    const changeShippingMethodsValue = (key: ShippingMethodsEditKeysType, value: any, error: boolean, id: number) => {
        const newData = [...shippingMethodsTableData];
        const productIndex = newData.findIndex((product) => product.ID === id);
        if (productIndex !== -1) {
            newData[productIndex] = { ...newData[productIndex], [key]: value };
        }
        setShippingMethodsTableData(newData);
    };

    const changePaymentMethodsValue = (key: MappingPaymentsEditKeysType, value: any, error: boolean, id: number) => {
        const newData = [...paymentMethodsTableData];
        const productIndex = newData.findIndex((product) => product.ID === id);
        if (productIndex !== -1) {
            newData[productIndex] = { ...newData[productIndex], [key]: value };
        }
        setPaymentMethodsTableData(newData);
    };

    const changeStatusMappingValues = (changes: { key: EditKeysType, value: any, error: boolean }[], id: number) => {
        const newData = [...statusesTableData];
        const productIndex = newData.findIndex((product) => product.ID === id);
        if (productIndex !== -1) {
            changes.forEach(change => {
                newData[productIndex] = { ...newData[productIndex], [change.key]: change.value };
            });
        }
        setStatusesTableData(newData);
    };

    const changePaymentMethodValues = (changes: {
        key: MappingPaymentsEditKeysType,
        value: any,
        error: boolean
    }[], id: number) => {
        const newData = [...paymentMethodsTableData];
        const productIndex = newData.findIndex((product) => product.ID === id);
        if (productIndex !== -1) {
            changes.forEach(change => {
                newData[productIndex] = { ...newData[productIndex], [change.key]: change.value };
            });
        }
        setPaymentMethodsTableData(newData);
    };

    const changeProductTypeValue = (value: string) => {
        setProductTypeData(prev => {
            if (!prev) return {
                UID: value,
                VALUE: value,
                NAME: value,
                KEY: 'PRODUCT_CUSTOM',
                IMPORT: '1',
                EXPORT: '1',
                ID: -1
            };

            return {
                ...prev,
                UID: value,
                VALUE: value,
                NAME: value
            };
        });
    };

    const changeOrderTypeValue = (key: OthersEditKeysType, value: any, error: boolean, id: number) => {
        // Použijeme funkční aktualizaci stavu, abychom vždy pracovali s nejnovějším stavem
        setOrderTypeData(prevData => {
            if (!prevData) return prevData;

            const newData = [...prevData];
            const itemIndex = newData.findIndex((item) => item.ID === id);

            if (itemIndex !== -1) {
                newData[itemIndex] = { ...newData[itemIndex], [key]: value };
            } else {
                console.warn(`Could not find order type with ID ${id} to update`);
            }

            return newData;
        });
    };

    const addOrderType = () => {
        // Použijeme funkční aktualizaci stavu, abychom vždy pracovali s nejnovějším stavem
        setOrderTypeData(prevData => {
            const existingData = prevData || [];

            // Generujeme unikátní ID na základě aktuálního stavu
            const newId = existingData.length > 0
                ? Math.min(...existingData.map(item => item.ID || 0), 0) - 1
                : -1;

            const newOrderType: SkladonMappingType = {
                UID: '',
                VALUE: 'B2B',
                NAME: 'B2B',
                KEY: 'ORDER_TYPE',
                IMPORT: '1',
                EXPORT: '1',
                ID: newId
            };

            return [...existingData.map(item => ({
                ...item,
                NAME: item.NAME?.toUpperCase(),
                VALUE: item.VALUE?.toUpperCase()
            })), newOrderType];
        });
    };

    const removeOrderType = (id: number) => {

        // Použijeme funkční aktualizaci stavu, abychom vždy pracovali s nejnovějším stavem
        setOrderTypeData(prevData => {
            if (!prevData) {
                console.warn('orderTypeData is null or undefined');
                return prevData;
            }

            // Find the item to be removed
            const itemToRemove = prevData.find(item => item.ID === id);

            // Remove from state
            const filteredData = prevData.filter(item => item.ID !== id);

            // If the item has a positive ID (exists in the database), mark it for deletion
            if (itemToRemove && itemToRemove.ID !== undefined && itemToRemove.ID > 0) {
                setOrderTypeIdsToDelete(prev => {
                    return [...prev, itemToRemove.ID as number];
                });
            }

            return filteredData;
        });
    };

    const changeRetinoValue = (key: OthersEditKeysType, value: any, error: boolean, id: number) => {
        // Použijeme funkční aktualizaci stavu, abychom vždy pracovali s nejnovějším stavem
        setRetinoData(prevData => {
            if (!prevData) return prevData;

            const newData = [...prevData];
            const itemIndex = newData.findIndex((item) => item.ID === id);
            if (itemIndex !== -1) {
                newData[itemIndex] = { ...newData[itemIndex], [key]: value };
            }

            return newData;
        });
    };

    const addRetinoMapping = () => {
        // Použijeme funkční aktualizaci stavu, abychom vždy pracovali s nejnovějším stavem
        setRetinoData(prevData => {
            const existingData = prevData || [];

            // Generujeme unikátní ID na základě aktuálního stavu
            const newId = existingData.length > 0
                ? Math.min(...existingData.map(item => item.ID || 0), 0) - 1
                : -1;

            const newRetinoItem: SkladonMappingType = {
                UID: '',
                VALUE: '',
                NAME: '',
                KEY: 'RETINO_ITEM_TYPE',
                SYS: 'RETINO',
                IMPORT: '1',
                EXPORT: '1',
                ID: newId
            };

            return [...existingData, newRetinoItem];
        });
    };

    const removeRetinoMapping = (id: number) => {
        // Použijeme funkční aktualizaci stavu, abychom vždy pracovali s nejnovějším stavem
        setRetinoData(prevData => {
            if (!prevData) {
                return prevData;
            }

            // Find the item to be removed
            const itemToRemove = prevData.find(item => item.ID === id);

            // Remove from state
            const filteredData = prevData.filter(item => item.ID !== id);

            // If the item has a positive ID (exists in the database), mark it for deletion
            if (itemToRemove && itemToRemove.ID !== undefined && itemToRemove.ID > 0) {
                setRetinoIdsToDelete(prev => {
                    return [...prev, itemToRemove.ID as number];
                });
            }
            return filteredData;
        });
    };

    const disableEdit = false;

    const backUrl = '/integration/';

    useEffect(() => {
        if (idProvider) {
            dispatch(
                setLocationParams({
                    name: translate('providerDetail.title', { idProvider, eshopName: provider?.COMPANY_NAME || '' }),
                    backUrl
                })
            );
        } else {
            dispatch(setLocationParams({ name: translate('providerDetail.titleNew'), backUrl }));
        }
    }, [idProvider, backUrl, locationHistory, provider]);

    useEffect(() => {
        if (activeTab === 'shippingMethods' && provider?.SYSTEM === 'SHOPTET') {
            dispatch(getShoptetShippingMethodsFetch(idProvider));
        } else if (activeTab === 'paymentMethods' && provider?.SYSTEM === 'SHOPTET') {
            dispatch(getShoptetPaymentMethodsFetch(idProvider));
        } else if (activeTab === 'paymentMethods' && provider?.SYSTEM === 'SHOPIFY') {
            dispatch(getShopifyPaymentMethodsFetch(idProvider));
        } else if (activeTab === 'shippingMethods' && provider?.SYSTEM === 'SHOPIFY') {
            dispatch(getShopifyShippingMethodsFetch(idProvider));
        } else if (activeTab === 'paymentMethods' && provider?.SYSTEM === 'WOOCOMMERCE') {
            dispatch(getWoocommercePaymentMethodsFetch(idProvider));
        } else if (activeTab === 'shippingMethods' && provider?.SYSTEM === 'WOOCOMMERCE') {
            dispatch(getWoocommerceShippingMethodsFetch(idProvider));
        } else if (activeTab === 'statuses' && provider?.SYSTEM === 'SHOPTET') {
            dispatch(getShoptetStatusesFetch(idProvider));
        }
    }, [idProvider, activeTab, provider]);

    useEffect(() => {
        if (idProvider) {
            dispatch(getProviderDetailFetch(idProvider));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearProviderDetail());
        };
    }, [idProvider]);

    // Načteme SHOPTET stavy ihned po načtení providera (pouze jednou)
    useEffect(() => {
        if (provider?.SYSTEM === 'SHOPTET' && idProvider && !shoptetStatuses) {
            dispatch(getShoptetStatusesFetch(idProvider));
        }
    }, [provider, idProvider, shoptetStatuses]);

    useEffect(() => {
        if (provider) {
            // Nejprv vyčistíme stará data, aby se nezobrazovala při změně providera
            setStatusesTableData([]);
            setShippingMethodsTableData([]);
            setPaymentMethodsTableData([]);
            setProductTypeData(null);
            setOrderTypeData([]);
            setRetinoData([]);
            setProviderServiceCodes([]);

            // Reset loading states
            setIsStatusesTableDataChanging(false);
            setIsShippingTableDataChanging(false);
            setIsPaymentTableDataChanging(false);
            setIsOthersTableDataChanging(false);

            dispatch(getSkladonMappingListFetch(provider.ID));
            getProviderServiceCodesFetchRequest(provider.PUBLIC_ID!).then((data) => {
                setProviderServiceCodes(data);
            });
        }
    }, [provider, idProvider]);

    useEffect(() => {
        if (wasSavingRef.current && !saveLoading && !integrationSaveError && provider) {
            dispatch(getSkladonMappingListFetch(provider.ID));
            setHistoryReloadKey((k) => k + 1);
        }
        wasSavingRef.current = saveLoading;
    }, [saveLoading, integrationSaveError, provider, dispatch]);

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (
            activeTab !== 'history' &&
            !Object.keys(newFormErrors).length &&
            provider &&
            !skladonMappingDataLoading &&
            !saveLoading &&
            !isStatusesTableDataChanging &&
            !isShippingTableDataChanging &&
            !isPaymentTableDataChanging &&
            !isOthersTableDataChanging
        ) {
            const basePayload: Record<string, unknown> = { section: activeTab, provider };

            if (activeTab === 'statuses') {
                basePayload.statusesData = statusesTableData;
            } else if (activeTab === 'shippingMethods') {
                basePayload.shippingMethodsData = shippingMethodsTableData;
            } else if (activeTab === 'paymentMethods') {
                basePayload.paymentMethodsData = paymentMethodsTableData;
            } else if (activeTab === 'others') {
                const othersData: SkladonMappingType[] = [];
                if (productTypeData) {
                    othersData.push(productTypeData);
                }
                if (orderTypeData && orderTypeData.length > 0) {
                    othersData.push(...orderTypeData);
                }
                const retinoDataToSave: SkladonMappingType[] = [];
                if (hasRetinoConfig && retinoData && retinoData.length > 0) {
                    retinoDataToSave.push(...retinoData);
                }
                basePayload.othersData = othersData;
                basePayload.retinoData = retinoDataToSave;
                basePayload.orderTypeIdsToDelete = [...orderTypeIdsToDelete];
                basePayload.retinoIdsToDelete = [...retinoIdsToDelete];
                setOrderTypeIdsToDelete([]);
                setRetinoIdsToDelete([]);
            }

            dispatch(saveSkladonMappingFetch(basePayload));
        }

        setEditErrors(newFormErrors);
    };


    return (
        <>
            <TopBlock
                data={skladonMappingData}
                idProvider={idProvider}
                disableEdit={disableEdit || skladonMappingDataLoading || saveLoading}
                handleSave={handleSave}
                activeTab={activeTab}
                handleChangeTab={setActiveTab}
            />
            {activeTab === 'history' && idProvider ? (
                <IntegrationMappingHistory
                    providerId={Number(idProvider)}
                    reloadKey={historyReloadKey}
                    formatContext={integrationHistoryFormatContext}
                />
            ) : (
                <form ref={formRef}>
                    {skladonMappingDataLoading && <Loading />}
                    <RenderComponents
                        statusesData={statusesTableData}
                        activeTab={activeTab}
                        boolNumberCodes={boolNumberCodes}
                        orderStatuses={orderStatusCodes}
                        changeStatusesValue={changeStatusMappingValue}
                        changeStatusesValues={changeStatusMappingValues}
                        shippingMethodsData={shippingMethodsTableData}
                        changeShippingMethodsValue={changeShippingMethodsValue}
                        shipperCodes={shipperCodes}
                        shipperServiceCodes={serviceCodes}
                        providerServiceCodes={providerServiceCodes}
                        paymentMethodsData={paymentMethodsTableData}
                        paymentMethodCodes={paymentMethodCodes}
                        changePaymentMethodsValue={changePaymentMethodsValue}
                        changePaymentMethodValues={changePaymentMethodValues}
                        productTypeData={productTypeData}
                        orderTypeData={orderTypeData}
                        changeProductTypeValue={changeProductTypeValue}
                        changeOrderTypeValue={changeOrderTypeValue}
                        addOrderType={addOrderType}
                        removeOrderType={removeOrderType}
                        statusesLoading={shoptetStatusesLoading || skladonMappingDataLoading || isStatusesTableDataChanging}
                        shippingMethodsLoading={shoptetShippingMethodsLoading || skladonMappingDataLoading || shopifyShippingMethodsLoading || isShippingTableDataChanging}
                        paymentMethodsLoading={shoptetPaymentMethodsLoading || shopifyPaymentMethodsLoading || skladonMappingDataLoading || isPaymentTableDataChanging}
                        othersLoading={skladonMappingDataLoading || isOthersTableDataChanging}
                        retinoData={retinoData}
                        changeRetinoValue={changeRetinoValue}
                        addRetinoMapping={addRetinoMapping}
                        removeRetinoMapping={removeRetinoMapping}
                        hasRetinoConfig={hasRetinoConfig}
                        providerSystem={provider?.SYSTEM}
                    />
                </form>
            )}

            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default IntegrationDetailContainer;
